import React from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { Typography, LinearProgress } from '@material-ui/core'
import { SavingAccount } from '../../../../types/savingAccount'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '2rem 0',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    margin: '1rem 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#7D7787',
    margin: '.5rem 0',
    fontWeight: 500,
  },
  cardContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    columnGap: '1rem',
    rowGap: '1rem',
    background: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '.8rem',
    padding: '2rem',
  },
  score: {
    fontSize: '3rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    '& > span': {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.palette.grey[600],
    },
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&>div': {
      width: '90%',
    },
    '&>p': {
      fontSize: '.8rem',
      fontWeight: 600,
    },
  },
  rating: {
    margin: '1rem 0',
    '&>p': {
      margin: '0',
    },
  },
}))

interface props {
  savingAccount: SavingAccount
}

const BorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 6,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[300],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
  },
}))(LinearProgress)

export default function KeyFacts({ savingAccount }: props): JSX.Element {
  const classes = useStyles({})

  const { features_rating, usability_rating, onboarding_rating, support_rating } = savingAccount
  const averageRating =
    (Number.parseFloat(features_rating) +
      Number.parseFloat(usability_rating) +
      Number.parseFloat(onboarding_rating) +
      Number.parseFloat(support_rating)) /
    4
  const score = (10 * averageRating) / 5

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.title}>
        Rating
      </Typography>
      <div className={classes.cardContainer}>
        <div>
          <Typography className={classes.subtitle}>Score</Typography>
          <Typography className={classes.score}>
            {score.toFixed(1)} <span>/ 10</span>
          </Typography>
        </div>
        <div>
          <Typography className={classes.subtitle}> Rating Breakdown </Typography>

          <div className={classes.rating}>
            <Typography className={classes.subtitle}>Features</Typography>
            <div className={classes.progressContainer}>
              <div>
                <BorderLinearProgress
                  variant="determinate"
                  value={(Number.parseFloat(features_rating) / 5) * 100}
                />
              </div>
              <Typography variant="body2">{features_rating}</Typography>
            </div>
          </div>

          <div className={classes.rating}>
            <Typography className={classes.subtitle}>Usability</Typography>
            <div className={classes.progressContainer}>
              <div>
                <BorderLinearProgress
                  variant="determinate"
                  value={(Number.parseFloat(usability_rating) / 5) * 100}
                />
              </div>
              <Typography variant="body2">{usability_rating}</Typography>
            </div>
          </div>

          <div className={classes.rating}>
            <Typography className={classes.subtitle}>Onboarding</Typography>
            <div className={classes.progressContainer}>
              <div>
                <BorderLinearProgress
                  variant="determinate"
                  value={(Number.parseFloat(onboarding_rating) / 5) * 100}
                />
              </div>
              <Typography variant="body2">{onboarding_rating}</Typography>
            </div>
          </div>

          <div className={classes.rating}>
            <Typography className={classes.subtitle}>Support</Typography>
            <div className={classes.progressContainer}>
              <div>
                <BorderLinearProgress
                  variant="determinate"
                  value={(Number.parseFloat(support_rating) / 5) * 100}
                />
              </div>
              <Typography variant="body2">{support_rating}</Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
